/*
  paymentMath.js — the mortgage-payment engine shared by the calculators.

  WHY IT IS ITS OWN FILE
  The Payment Calculator and the Scenario Builder compute the same numbers. Two copies of the
  FHA tier tables would eventually disagree, and the way you'd find out is a borrower being
  quoted two different MI figures by two pages of the same app. One copy, imported by both.

  PROVENANCE
  Every constant is copied from CGS2022 Scripts/Calculator/Calculator.vue and CalculatorV2.vue,
  not re-derived from a rate sheet. Where this file departs from CGS it says so in a comment.

  UNITS — the thing that silently produces plausible-but-wrong numbers:
    annualPct is a PERCENT (7.5), never a decimal.
    months is MONTHS (360), never years.
  CGS is inconsistent about this internally (V1 stores the rate as a decimal, V2 as a percent,
  and one helper divides by 100 while its neighbour does not). Everything here is percent.
*/

// ── rate constants, verbatim from CGS ──────────────────────────────────────────
export const FHA_UPFRONT_PURCHASE = 0.0175
export const FHA_UPFRONT_REFI = 0.0001    // yes, one basis point -- that is what the source has
export const FHA_MONTHLY_REFI = 0.0055
export const FHA_HIGH_BALANCE_CUTOFF = 625500
export const USDA_UPFRONT = 0.01
export const USDA_MONTHLY = 0.0035
export const VA_REFI_RATE = 0.005

export const LOAN_TYPES = [
  { key: 'conv', label: 'Conventional' },
  { key: 'fha', label: 'FHA' },
  { key: 'va', label: 'VA' },
  { key: 'usda', label: 'USDA' }
]
export const REFI_TYPES = ['Cash Out', 'No Cash Out']
export const VA_TYPES = ['Regular Military/Active Duty', 'Reserves', 'National Guard', 'Exempt']
// CGS ships this list with "Graduated Payament" misspelled. Fixed here; it is display-only and
// nothing branches on the string.
export const AMORT_TYPES = ['Fixed', 'Adjustable Rate', 'Graduated Payment', 'Growing Equity Mortgage', 'Other']

// Forgiving on purpose: the inputs hold whatever the user typed, commas and dollar signs and
// half-finished decimals included.
export function num (v) {
  const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

export function hasValue (v) {
  return String(v == null ? '' : v).trim() !== ''
}

// Standard amortizing payment. CGS puts .toFixed(2) on the DENOMINATOR rather than the result,
// which quietly rounded the divisor; corrected here.
export function pmt (principal, annualPct, months) {
  const r = annualPct / 100 / 12
  if (!(principal > 0 && months > 0)) return 0
  if (r <= 0) return principal / months   // a genuine 0% loan is straight-line
  const pow = Math.pow(1 + r, months)
  return (principal * r * pow) / (pow - 1)
}

/*
  The financed up-front fee: FHA UFMIP, the VA funding fee, or the USDA guarantee fee.

  `baseLtv` is the LTV BEFORE the fee is financed. CGS reads its `ltv` computed here, which is
  derived from `loanAmount`, which is derived from this fee — a real circular dependency that
  only resolved through Vue's side-effect ordering and a stale value. Taking the tier off the
  pre-fee loan is deterministic, and it is also how the fee actually works.
*/
export function upFrontFee ({ loanType, isBuy, vaType, firstTime, baseLoanAmount, baseLtv }) {
  const base = num(baseLoanAmount)
  if (base <= 0) return 0

  if (loanType === 'fha') return base * (isBuy ? FHA_UPFRONT_PURCHASE : FHA_UPFRONT_REFI)
  if (loanType === 'usda') return base * USDA_UPFRONT

  if (loanType === 'va') {
    if (vaType === 'Exempt') return 0
    if (!isBuy) return base * VA_REFI_RATE

    const ltv = num(baseLtv)
    const regular = vaType === 'Regular Military/Active Duty'
    let r
    if (regular) {
      if (firstTime) r = ltv > 95 ? 0.0215 : ltv >= 90 ? 0.015 : 0.0125
      else r = ltv > 95 ? 0.033 : ltv >= 90 ? 0.015 : 0.0125
    } else {
      if (firstTime) r = ltv > 95 ? 0.024 : ltv >= 90 ? 0.0175 : 0.015
      else r = ltv > 95 ? 0.033 : ltv >= 90 ? 0.0175 : 0.015
    }
    return base * r
  }
  return 0
}

export function upFrontFeeLabel (loanType) {
  if (loanType === 'fha') return 'Up-front MIP (financed)'
  if (loanType === 'va') return 'VA funding fee (financed)'
  if (loanType === 'usda') return 'USDA guarantee fee (financed)'
  return 'Up-front fee'
}

/*
  Monthly mortgage insurance. Conventional is NOT derived — it is whatever factor was quoted,
  which is why the caller passes `monthlyMI` straight through. The FHA tiers key off the loan
  amount (not the price) against the high-balance cutoff, then term and LTV.
*/
export function monthlyMi ({ loanType, isBuy, baseLoanAmount, ltv, termMonths, monthlyMI }) {
  const base = num(baseLoanAmount)
  const l = num(ltv)
  const term = num(termMonths)

  if (loanType === 'fha') {
    let annual
    if (!isBuy) {
      annual = FHA_MONTHLY_REFI
    } else if (base <= FHA_HIGH_BALANCE_CUTOFF) {
      annual = term > 180 ? (l > 95 ? 0.0085 : 0.008) : (l > 90 ? 0.007 : 0.0045)
    } else {
      annual = term > 180
        ? (l > 95 ? 0.0105 : 0.01)
        : (l > 90 ? 0.0095 : l > 78 ? 0.007 : 0.0045)
    }
    // CGS drops MI entirely on a short term at low LTV.
    if (term <= 180 && l <= 78) return 0
    return (base * annual) / 12
  }

  if (loanType === 'usda') return (base * USDA_MONTHLY) / 12
  if (loanType === 'va') return 0
  return num(monthlyMI)
}

/*
  APR by bisection on the monthly rate that discounts the payment stream back to the amount
  financed.

  CGS V1's calculateAPR was `(loanAmount * noteRate) * (term / 12)` divided about, and the
  source carries two "I need Clarification on how GS calculates it" comments against it. This
  is the method CalculatorV2 uses, and it returns the note rate exactly when there are no fees
  — which is the sanity check worth keeping.
*/
export function solveApr ({ loanAmount, payment, termMonths, fees }) {
  const principal = num(loanAmount)
  const pay = num(payment)
  const n = num(termMonths)
  const financed = principal - num(fees)
  if (!(principal > 0 && pay > 0 && n > 0 && financed > 0)) return 0

  let low = 0
  let high = 1
  let r = 0
  for (let i = 0; i < 200; i++) {
    r = (low + high) / 2
    if (r <= 0) break
    const pv = pay * ((1 - Math.pow(1 + r, -n)) / r)
    const diff = pv - financed
    if (Math.abs(diff) < 0.01) break
    if (diff > 0) low = r
    else high = r
  }
  return r * 12 * 100
}

// Month-by-month schedule. The final payment absorbs whatever is left rather than overshooting
// into a negative balance. Capped at the term, so a payment too small to amortize returns the
// rows it managed rather than spinning.
export function buildAmortization ({ loanAmount, annualPct, termMonths, payment }) {
  const r = num(annualPct) / 100 / 12
  const n = Math.round(num(termMonths))
  const pay = num(payment)
  if (!(pay > 0 && n > 0)) return []

  const rows = []
  let balance = num(loanAmount)
  for (let i = 0; i < n && balance > 0.01; i++) {
    const interest = balance * r
    let principal = pay - interest
    if (principal <= 0) break     // the payment does not even cover interest
    if (principal >= balance) {
      principal = balance
      balance = 0
    } else {
      balance -= principal
    }
    rows.push({ interest, principal, balance })
  }
  return rows
}

export function totalInterest (rows) {
  return (rows || []).reduce((s, row) => s + row.interest, 0)
}

/*
  One call that turns a set of inputs into every derived figure, so the two calculators cannot
  disagree about the order things are computed in. Inputs are the raw strings the form holds.
*/
export function priceScenario (input) {
  const isBuy = input.loanPurpose === 'Buy'
  const price = num(input.purchasePrice)
  const termMonths = Math.round(num(input.term))
  const annualPct = num(input.noteRatePct)

  const baseLoanAmount = isBuy
    ? Math.max(0, price - num(input.downPayment))
    : num(input.amountOwed) + num(input.cashOut)

  const baseLtv = price > 0 ? (baseLoanAmount / price) * 100 : 0

  const fee = upFrontFee({
    loanType: input.loanType,
    isBuy,
    vaType: input.vaType,
    firstTime: input.firstTime,
    baseLoanAmount,
    baseLtv
  })

  // Conventional finances nothing up front; the others roll the fee into the loan.
  const loanAmount = baseLoanAmount + fee
  const ltv = price > 0 ? (loanAmount / price) * 100 : 0

  // A BLANK rate field is not a 0% loan. Without this, pmt()'s zero-rate branch straight-lines
  // the principal and the page shows a confident payment before anyone entered a rate.
  const isPriced = hasValue(input.noteRatePct) && termMonths > 0
  const pandi = isPriced ? pmt(loanAmount, annualPct, termMonths) : 0

  const pmi = monthlyMi({
    loanType: input.loanType,
    isBuy,
    baseLoanAmount,
    ltv,
    termMonths,
    monthlyMI: input.monthlyMI
  })

  const taxes = num(input.taxes)
  const insurance = num(input.insurance)
  const hoa = num(input.hoa)
  const totalPayment = pandi + pmi + taxes + insurance + hoa

  // Only a fixed loan has a schedule worth drawing; CGS makes the same call.
  const amortization = input.loanAmort === 'Fixed'
    ? buildAmortization({ loanAmount, annualPct, termMonths, payment: pandi })
    : []

  return {
    isBuy,
    isPriced,
    baseLoanAmount,
    baseLtv,
    upFrontFee: fee,
    upFrontFeeLabel: upFrontFeeLabel(input.loanType),
    loanAmount,
    ltv,
    termMonths,
    annualPct,
    pandi,
    pmi,
    taxes,
    insurance,
    hoa,
    totalPayment,
    apr: solveApr({ loanAmount, payment: pandi, termMonths, fees: num(input.additionalFees) + fee }),
    amortization,
    totalInterestPaid: totalInterest(amortization)
  }
}

// "360" -> "30 Years", "342" -> "28.5 Years". CGS builds the default scenario name this way.
export function termLabel (months) {
  const m = Math.round(num(months))
  if (!m) return '0 Years'
  return (m % 12 === 0 ? String(m / 12) : (m / 12).toFixed(1)) + ' Years'
}

export function money (n) {
  return '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function pct (n) {
  return (Number(n) || 0).toFixed(3) + '%'
}

/*
  Temporary buydown — ported from CGS2022 Scripts/Calculator/buydownCalculator.vue.

  A "3-2-1" takes 3 points off the note rate in year one, 2 in year two, 1 in year three, then
  the borrower pays the full note-rate payment. The seller/lender funds the difference up front:
  each bought-down year's monthly shortfall x 12, summed, is the buydown fund (escrow).

  Same method as CGS: every year's payment is a full-term amortizing payment at the reduced
  rate, on the original balance. Where this departs from CGS:
  - CGS rounds every figure to whole dollars for display, and its fund is the sum of the
    UNROUNDED subsidies. Both are kept to the cent here; the fund is still summed unrounded.
  - CGS's getMonthlyPayment divides by zero on a 0% year, and happily prices a negative rate
    when the reduction exceeds the note rate. pmt() straight-lines 0%, and a reduction at or
    above the note rate is reported back as an error instead of priced.
  - "1-0" is a one-year buydown; the zero year is dropped, exactly as CGS's filter does.
*/
export const BUYDOWN_TYPES = ['3-2-1', '2-1', '1-1', '1-0']

export function buydownSchedule ({ loanAmount, noteRatePct, termMonths, type }) {
  const principal = num(loanAmount)
  const rate = num(noteRatePct)
  const n = Math.round(num(termMonths))
  const reductions = String(type || '').split('-').map(Number).filter(r => r > 0)

  const isPriced = principal > 0 && hasValue(noteRatePct) && n > 0
  const fullPayment = isPriced ? pmt(principal, rate, n) : 0

  const years = reductions.map((reduction, i) => {
    const yearRate = rate - reduction
    const payment = isPriced && yearRate >= 0 ? pmt(principal, yearRate, n) : 0
    const subsidy = payment > 0 ? fullPayment - payment : 0
    return { year: i + 1, reduction, rate: yearRate, payment, subsidy, annualSubsidy: subsidy * 12 }
  })

  const error = isPriced && reductions.length && rate - reductions[0] < 0
    ? `A ${type} buydown takes ${reductions[0]} points off in year one, which is more than the ${rate.toFixed(3)}% note rate.`
    : ''

  return {
    isPriced: isPriced && !error,
    error,
    fullPayment,
    years,
    buydownFund: error ? 0 : years.reduce((s, y) => s + y.annualSubsidy, 0)
  }
}
